/** Import a company list (with career pages) from CSV — thousands at once. */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { api } from '@/api/client'
import type { CompanyCategory, ImportResult, Market } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Badge, ErrorBox, Label, Modal, Select } from '@/components/ui/Primitives'
import { errorMessage, toast } from '@/components/ui/Toast'
import { CATEGORIES, CATEGORY_LABEL, cn } from '@/lib/utils'

const TEMPLATE = [
  'company,career_url,website,market,category,tier,industry,headquarters',
  'Example Corp,https://careers.example.com,https://example.com,India,GCC,1,Software,United States',
  'Example Corp,,,USA,MNC,1,,',
  'Example Startup,https://jobs.lever.co/example,https://example.in,India,Startup,3,Fintech,India',
].join('\n')

export function ImportCompaniesModal({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [market, setMarket] = useState<Market | ''>('')
  const [category, setCategory] = useState<CompanyCategory>('mnc')
  const input = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()
  const upload = useMutation({
    mutationFn: () => api.importCompanies(file as File, market, category),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['companies'] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
      toast.success(`${r.companies_created.toLocaleString()} companies added, ${r.career_pages_added.toLocaleString()} career pages queued`)
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const pick = (f: File | undefined) => {
    if (f) {
      setFile(f)
      upload.reset()
    }
  }
  const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE)}`

  return (
    <Modal title="Import companies" onClose={onClose} width="max-w-2xl">
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            pick(e.dataTransfer.files[0])
          }}
          className={cn(
            'flex w-full flex-col items-center justify-center rounded-field border-2 border-dashed px-6 py-9 text-center transition',
            dragging ? 'border-ink-soft bg-surface-muted' : 'border-line hover:border-line-strong',
          )}
        >
          <span className="text-[14.5px] font-medium">{file ? file.name : 'Drop a CSV here, or click to choose'}</span>
          <span className="mt-1 text-[12.5px] text-ink-muted">
            {file ? `${(file.size / 1024).toFixed(0)} KB` : 'Up to 50 MB · existing companies are updated, never duplicated'}
          </span>
        </button>
        <input ref={input} type="file" accept=".csv,text/csv" hidden onChange={(e) => pick(e.target.files?.[0])} />

        <div className="rounded-field bg-surface-muted px-4 py-3 text-[12.5px] leading-relaxed text-ink-muted">
          <p className="mb-1 font-medium text-ink-soft">Columns (header row, any order)</p>
          <code className="font-mono">company</code> (required) · <code className="font-mono">career_url</code> ·{' '}
          <code className="font-mono">website</code> · <code className="font-mono">market</code> (India / USA) ·{' '}
          <code className="font-mono">category</code> (MNC / GCC / Domestic / Startup) · <code className="font-mono">tier</code> (1–3) ·{' '}
          <code className="font-mono">industry</code> · <code className="font-mono">headquarters</code>.
          <br />A company in both countries = two rows with the same name (or use <code className="font-mono">india_category</code>,{' '}
          <code className="font-mono">india_tier</code>, <code className="font-mono">us_category</code>, <code className="font-mono">us_tier</code>).{' '}
          <a className="link" href={templateHref} download="companies-template.csv">
            Download template
          </a>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <Label hint="rows without a market">Country</Label>
            <Select value={market} onChange={(e) => setMarket(e.target.value as Market | '')} className="w-full">
              <option value="">Required in the file</option>
              <option value="IN">India</option>
              <option value="US">USA</option>
            </Select>
          </label>
          <label className="block">
            <Label hint="rows without a category">Category</Label>
            <Select value={category} onChange={(e) => setCategory(e.target.value as CompanyCategory)} className="w-full">
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </Select>
          </label>
        </div>

        {upload.isError && <ErrorBox error={upload.error} />}
        {upload.data && <Summary result={upload.data} />}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            {upload.data ? 'Done' : 'Cancel'}
          </Button>
          <Button disabled={!file} loading={upload.isPending} onClick={() => upload.mutate()}>
            Import
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function Summary({ result }: { result: ImportResult }) {
  return (
    <div className="rounded-field border border-line bg-surface-muted/60 px-4 py-3 text-[13.5px]">
      <div className="flex flex-wrap gap-2">
        <Badge tone="success">{result.companies_created.toLocaleString()} new companies</Badge>
        {result.companies_updated > 0 && <Badge>{result.companies_updated.toLocaleString()} updated</Badge>}
        <Badge tone="info">{result.career_pages_added.toLocaleString()} career pages queued</Badge>
        {result.duplicate_career_pages > 0 && <Badge>{result.duplicate_career_pages.toLocaleString()} career pages already tracked</Badge>}
        {result.invalid_rows > 0 && <Badge tone="danger">{result.invalid_rows.toLocaleString()} rows skipped</Badge>}
      </div>
      {result.invalid_samples.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[12.5px] text-danger">
          {result.invalid_samples.slice(0, 6).map((s) => (
            <li key={s}>• {s}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
